import isEmpty from 'lodash/isEmpty';
import { GetStaticProps, NextPage } from 'next';
import React from 'react';

import HeadMeta from '@/components/static-pages/HeadMeta';
import GetOuttaHereTemplate, {
  IGOHProps,
  IPartnerCardInfo,
  IVehicleCardInfo,
} from '@/components/templates/get-outta-here/GetOuttaHereTemplate';
import { getStaticPageData } from '@/services/getPageData';
import { logger } from '@/utility/logger';

import { IBlockType, ImageBlockType } from '../loans';

const GOH_THUMBNAIL_URL =
  'https://outdoorsy-cms.s3.amazonaws.com/media/original_images/GOH_LP_Thumb_1.png';

const GetOuttaHere: NextPage<IGOHProps> = props => {
  return (
    <>
      <HeadMeta
        title={props.seoTitle}
        description={props.seoDescription}
        noindex={props.noindex}
        thumbnailURL={GOH_THUMBNAIL_URL}
      />
      <GetOuttaHereTemplate {...props} />
    </>
  );
};

export const getStaticProps: GetStaticProps<IGOHProps> = async ({ locale }) => {
  const pageData = await getStaticPageData({ locale, url_path: 'get-outta-here-content' });
  if (isEmpty(pageData) || isEmpty(pageData.metadata)) {
    return {
      notFound: true,
      revalidate: true,
    };
  }

  const { seo_title, seo_description, hero_image, subheading, content_blocks, button_label } =
    pageData.metadata;

  const gohLogo = content_blocks[0].value;
  const carouselImageUrls: ImageBlockType[] = content_blocks
    .filter((i: IBlockType) => i.type === 'image')
    .slice(1) // first image is the logo
    .map((i: IBlockType) => i.value);

  const splitBlocks = content_blocks.filter((i: IBlockType) => i.type === 'split');
  const partnerCardInfo: IPartnerCardInfo[] = [];
  const vehicleCardInfo: IVehicleCardInfo[] = [];

  for (const block of splitBlocks) {
    try {
      if (block.value.left[0] && block.value.left[0].type === 'image') {
        const partnerInfo: IPartnerCardInfo = {
          partnerCardImage: block.value.left[0].value,
          partnerLogo: block.value.right[0].value,
          partnerHeading: block.value.right[1].value,
          partnerDescription: block.value.right[2].value,
        };
        partnerCardInfo.push(partnerInfo);
      } else if (block.value.left && block.value.left.length === 2) {
        const vehicleInfo: IVehicleCardInfo = {
          primaryHeading: block.value.left[0].value,
          secondaryHeading: block.value.left[1].value,
          primaryImage: block.value.right[0].value,
          secondaryImages: [block.value.right[1].value, block.value.right[2].value],
        };
        vehicleCardInfo.push(vehicleInfo);
      }
    } catch (error) {
      logger.log('Error ', error);
    }
  }

  return {
    props: {
      seoTitle: seo_title,
      seoDescription: seo_description,
      hero_image,
      subheading,
      gohLogo,
      carouselImageUrls,
      partnerCardInfo,
      vehicleCardInfo,
      noindex: !!pageData.fallback,
      button_label,
    },
    revalidate: 180,
  };
};

export default GetOuttaHere;
